import type { TextKey, Translated } from "@repo/shared/types";
import {
  type DashboardPeriod,
  type DashboardSortDirection,
  type DashboardUserRow,
  type DashboardUserSortKey,
  DEFAULT_DASHBOARD_PERIOD,
} from "@repo/shared/types";
import { ChevronRight, UsersRound } from "lucide-react";
import { useState } from "react";
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { useDashboardFormat } from "../use-dashboard-format";
import { InitialsAvatar } from "./initials-avatar";
import { PeriodSwitch } from "./period-switch";
import { T } from "./users-widget.i18n";
import { WidgetCard } from "./widget-card";
import { WidgetEmpty } from "./widget-empty";

type Label = TextKey<Translated<typeof T>>;

type NumericColumn = Exclude<DashboardUserSortKey, "name">;

const COLUMNS: { key: NumericColumn; label: Label }[] = [
  { key: "aum", label: "colAum" },
  { key: "savingInflow", label: "colInflow" },
  { key: "activities", label: "colActivities" },
  { key: "meetings", label: "colMeetings" },
  { key: "calls", label: "colCalls" },
  { key: "notes", label: "colNotes" },
  { key: "flowEvents", label: "colFlowEvents" },
];

type UsersWidgetProps = {
  target: DashboardTarget;
  /** Open a user's own dashboard. */
  userHref: (userId: string) => string;
};

/**
 * The top users by any column. Ranking happens on the server before the top 15
 * are cut, so clicking a column can bring in users the first view left out.
 */
export function UsersWidget({ target, userHref }: UsersWidgetProps) {
  const [period, setPeriod] = useState<DashboardPeriod>(DEFAULT_DASHBOARD_PERIOD);
  const [sort, setSort] = useState<{ by: DashboardUserSortKey; dir: DashboardSortDirection }>({
    by: "aum",
    dir: "desc",
  });
  const { data, isLoading, error } = useDashboardWidget("users", target, {
    period,
    sortBy: sort.by,
    sortDir: sort.dir,
  });
  const t = useT(T);
  const { count, amount } = useDashboardFormat();

  // Same column flips the direction; a new one starts highest first (names A to Z).
  const sortBy = (by: DashboardUserSortKey) =>
    setSort((current) =>
      current.by === by
        ? { by, dir: current.dir === "asc" ? "desc" : "asc" }
        : { by, dir: by === "name" ? "asc" : "desc" },
    );
  const direction = (by: DashboardUserSortKey) => (sort.by === by ? sort.dir : null);
  const maxAum = Math.max(0, ...(data?.rows.map((r) => r.aum) ?? []));

  const cell = (row: DashboardUserRow, key: NumericColumn) => {
    if (key === "aum") {
      return (
        <div className="flex flex-col items-end gap-1.5">
          <span className="font-medium">{amount(row.aum)}</span>
          <div className="h-1 w-30 overflow-hidden rounded-sm bg-muted">
            <div
              className="ml-auto h-full rounded-sm bg-brand"
              style={{ width: `${maxAum > 0 ? (row.aum / maxAum) * 100 : 0}%` }}
            />
          </div>
        </div>
      );
    }
    if (key === "savingInflow") return amount(row.savingInflow);
    return count(row[key]);
  };

  return (
    <WidgetCard
      icon={UsersRound}
      title={t.users}
      description={t.usersDescription}
      actions={<PeriodSwitch value={period} onChange={setPeriod} />}
      isLoading={isLoading}
      error={error}
    >
      {data &&
        (data.rows.length === 0 ? (
          <WidgetEmpty>{t.noUsers}</WidgetEmpty>
        ) : (
          <>
            <Table variant="navigation">
              <TableHeader>
                <TableRow>
                  <SortableTableHead direction={direction("name")} onSort={() => sortBy("name")}>
                    {t.colUser}
                  </SortableTableHead>
                  {COLUMNS.map((column) => (
                    <SortableTableHead
                      key={column.key}
                      className={cn("text-right", sort.by === column.key && "text-foreground")}
                      direction={direction(column.key)}
                      onSort={() => sortBy(column.key)}
                    >
                      {t[column.label]}
                    </SortableTableHead>
                  ))}
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((row) => (
                  <TableRow key={row.userId}>
                    <TableCellLink
                      to={userHref(row.userId)}
                      linkClassName="flex items-center gap-3"
                    >
                      <InitialsAvatar name={row.name} />
                      <div>
                        <p className="font-medium">{row.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {row.clientCount === 1
                            ? t.clientCountOne
                            : t.clientCount(count(row.clientCount))}
                        </p>
                      </div>
                    </TableCellLink>
                    {COLUMNS.map((column) => (
                      <TableCell
                        key={column.key}
                        className={cn("text-right", column.key === "activities" && "font-semibold")}
                      >
                        {cell(row, column.key)}
                      </TableCell>
                    ))}
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{t.showingUsers(data.rows.length, data.totalUsers)}</span>
              <span>{t.amountsIn(data.currency)}</span>
            </div>
          </>
        ))}
    </WidgetCard>
  );
}
